import React, { useState } from 'react';
import { FaTimes, FaStar, FaImage, FaTrash } from 'react-icons/fa';
import '../../styles/FeedbackModal.css';

function FeedbackModal({ isOpen, onClose, onSubmit }) {
    const [feedbackType, setFeedbackType] = useState('feedback'); // 'feedback' or 'issue'
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [priority, setPriority] = useState('medium'); // 'low', 'medium', 'high', 'critical'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [images, setImages] = useState([]);

    // Dynamic validation functions
    const validateSubject = (value) => {
        if (!value.trim()) {
            return 'Subject is required';
        } else if (value.length > 100) {
            return 'Subject must be less than 100 characters';
        }
        return '';
    };

    const validateMessage = (value) => {
        if (!value.trim()) {
            return 'Message is required';
        } else if (value.length < 10) {
            return 'Message must be at least 10 characters long';
        } else if (value.length > 1000) {
            return 'Message must be less than 1000 characters';
        }
        return '';
    };

    const validateRating = (value) => {
        if (feedbackType === 'feedback' && value === 0) {
            return 'Please provide a rating';
        }
        return '';
    };

    // Dynamic input handlers
    const handleSubjectChange = (e) => {
        const value = e.target.value;
        setSubject(value);
        
        const error = validateSubject(value);
        setErrors(prev => ({
            ...prev,
            subject: error
        }));
    };

    const handleMessageChange = (e) => {
        const value = e.target.value;
        setMessage(value);
        
        const error = validateMessage(value);
        setErrors(prev => ({
            ...prev,
            message: error
        }));
    };

    const handleRatingChange = (newRating) => {
        setRating(newRating);
        
        const error = validateRating(newRating);
        setErrors(prev => ({
            ...prev,
            rating: error
        }));
    };

    const handleTypeChange = (newType) => {
        setFeedbackType(newType);
        
        // Clear rating error when switching to issue type
        if (newType === 'issue') {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.rating;
                return newErrors;
            });
        } else {
            // Re-validate rating for feedback type
            const error = validateRating(rating);
            setErrors(prev => ({
                ...prev,
                rating: error
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        const subjectError = validateSubject(subject);
        if (subjectError) newErrors.subject = subjectError;
        
        const messageError = validateMessage(message);
        if (messageError) newErrors.message = messageError;
        
        const ratingError = validateRating(rating);
        if (ratingError) newErrors.rating = ratingError;

        // Validate images
        images.forEach((image, index) => {
            if (image.size > 5 * 1024 * 1024) { // 5MB limit
                newErrors[`image${index}`] = 'Image size must be less than 5MB';
            }
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        
        const feedbackData = {
            type: feedbackType,
            subject: subject.trim(),
            message: message.trim(),
            rating: feedbackType === 'feedback' ? rating : null,
            priority: feedbackType === 'issue' ? priority : null,
            images: images.map(img => ({
                name: img.name,
                size: img.size,
                type: img.type,
                data: img.dataURL || null
            })),
            timestamp: new Date().toISOString(),
            page: window.location.pathname // Automatically detect current page
        };

        try {
            await onSubmit(feedbackData);
            // Reset form
            setFeedbackType('feedback');
            setSubject('');
            setMessage('');
            setRating(0);
            setHoverRating(0);
            setPriority('medium');
            setImages([]);
            setErrors({});
            onClose();
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setErrors({ submit: 'Failed to submit feedback. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFeedbackType('feedback');
            setSubject('');
            setMessage('');
            setRating(0);
            setHoverRating(0);
            setPriority('medium');
            setImages([]);
            setErrors({});
            onClose();
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        
        // Limit to 3 images total
        const remainingSlots = 3 - images.length;
        const filesToAdd = files.slice(0, remainingSlots);
        
        filesToAdd.forEach((file) => {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({ ...prev, images: 'Only image files are allowed' }));
                return;
            }
            
            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, images: 'Image size must be less than 5MB' }));
                return;
            }
            
            const reader = new FileReader();
            reader.onload = (event) => {
                const newImage = {
                    file,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    dataURL: event.target.result,
                    id: Date.now() + Math.random() // Unique ID
                };
                
                setImages(prev => [...prev, newImage]);
                // Clear any previous image errors
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.images;
                    return newErrors;
                });
            };
            reader.readAsDataURL(file);
        });
        
        // Reset input
        e.target.value = '';
    };

    const removeImage = (imageId) => {
        setImages(prev => prev.filter(img => img.id !== imageId));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const renderStars = () => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <FaStar
                    key={i}
                    className={`rating-star ${i <= (hoverRating || rating) ? 'filled' : ''}`}
                    onClick={() => handleRatingChange(i)}
                    onMouseEnter={() => setHoverRating(i)}
                    onMouseLeave={() => setHoverRating(0)}
                />
            );
        }
        return stars;
    };

    if (!isOpen) return null;

    return (
        <div className="feedback-modal-overlay" onClick={handleClose}>
            <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="feedback-modal-header">
                    <h2>{feedbackType === 'feedback' ? 'Send Feedback' : 'Report an Issue'}</h2>
                    <button 
                        className="feedback-close-btn" 
                        onClick={handleClose}
                        disabled={isSubmitting}
                        type="button"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="feedback-form">
                    <div className="feedback-field">
                        <label>Type</label>
                        <div className="type-selector">
                            <button
                                type="button"
                                className={`type-btn ${feedbackType === 'feedback' ? 'active' : ''}`}
                                onClick={() => handleTypeChange('feedback')}
                                disabled={isSubmitting}
                            >
                                💬 Feedback
                            </button>
                            <button
                                type="button"
                                className={`type-btn ${feedbackType === 'issue' ? 'active' : ''}`}
                                onClick={() => handleTypeChange('issue')}
                                disabled={isSubmitting}
                            >
                                🐛 Report Issue
                            </button>
                        </div>
                    </div>
                    <div className="feedback-field">
                        <label htmlFor="feedback-subject">Subject *</label>
                        <input
                            id="feedback-subject"
                            type="text"
                            value={subject}
                            onChange={handleSubjectChange}
                            placeholder="Brief description of your feedback..."
                            className={
                                errors.subject 
                                    ? 'error' 
                                    : subject.trim() && !validateSubject(subject) 
                                        ? 'success' 
                                        : ''
                            }
                            disabled={isSubmitting}
                            maxLength={100}
                        />
                        {errors.subject && <span className="error-text">{errors.subject}</span>}
                        {!errors.subject && subject.trim() && !validateSubject(subject) && (
                            <span className="success-text">✓ Subject looks good</span>
                        )}
                    </div>

                    {feedbackType === 'issue' && (
                        <div className="feedback-field">
                            <label htmlFor="priority-select">Priority</label>
                            <select
                                id="priority-select"
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                                disabled={isSubmitting}
                                className="priority-select"
                            >
                                <option value="low">🟢 Low - Minor issue, can wait</option>
                                <option value="medium">🟡 Medium - Standard issue</option>
                                <option value="high">🟠 High - Important issue</option>
                                <option value="critical">🔴 Critical - Blocking functionality</option>
                            </select>
                        </div>
                    )}

                    {feedbackType === 'feedback' && (
                        <div className="feedback-field">
                            <label>Rating *</label>
                            <div className="rating-container">
                                {renderStars()}
                                <span className="rating-text">
                                    {rating > 0 && (
                                        <>
                                            {rating === 1 && 'Poor'}
                                            {rating === 2 && 'Fair'}
                                            {rating === 3 && 'Good'}
                                            {rating === 4 && 'Very Good'}
                                            {rating === 5 && 'Excellent'}
                                        </>
                                    )}
                                </span>
                            </div>
                            {errors.rating && <span className="error-text">{errors.rating}</span>}
                        </div>
                    )}

                    <div className="feedback-field">
                        <label htmlFor="feedback-message">Message *</label>
                        <textarea
                            id="feedback-message"
                            value={message}
                            onChange={handleMessageChange}
                            placeholder={feedbackType === 'feedback' 
                                ? "Please share your detailed feedback, suggestions, or comments..."
                                : "Please describe the issue you encountered, including steps to reproduce if possible..."
                            }
                            className={
                                errors.message 
                                    ? 'error' 
                                    : message.trim() && !validateMessage(message) 
                                        ? 'success' 
                                        : ''
                            }
                            disabled={isSubmitting}
                            rows={6}
                            maxLength={1000}
                        />
                        <div className="char-counter">
                            {message.length}/1000 characters
                        </div>
                        {errors.message && <span className="error-text">{errors.message}</span>}
                        {!errors.message && message.trim() && !validateMessage(message) && (
                            <span className="success-text">✓ Message looks good</span>
                        )}
                    </div>

                    <div className="feedback-field">
                        <label htmlFor="feedback-images">Images (Optional - Up to 3)</label>
                        <div className="image-upload-container">
                            <input
                                id="feedback-images"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                disabled={isSubmitting || images.length >= 3}
                                className="image-upload-input"
                            />
                            <label 
                                htmlFor="feedback-images" 
                                className={`image-upload-label ${images.length >= 3 ? 'disabled' : ''}`}
                            >
                                <FaImage />
                                {images.length >= 3 ? 'Maximum 3 images' : 'Add Images'}
                            </label>
                        </div>
                        
                        {images.length > 0 && (
                            <div className="uploaded-images">
                                {images.map((image) => (
                                    <div key={image.id} className="image-preview">
                                        <img src={image.dataURL} alt={image.name} />
                                        <div className="image-info">
                                            <span className="image-name">{image.name}</span>
                                            <span className="image-size">{formatFileSize(image.size)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-image-btn"
                                            onClick={() => removeImage(image.id)}
                                            disabled={isSubmitting}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {errors.images && <span className="error-text">{errors.images}</span>}
                        
                        <div className="image-upload-help">
                            • Supported formats: JPG, PNG, GIF, WebP<br />
                            • Maximum size: 5MB per image<br />
                            • Up to 3 images allowed
                        </div>
                    </div>

                    {errors.submit && (
                        <div className="submit-error">
                            {errors.submit}
                        </div>
                    )}

                    <div className="feedback-actions">
                        <button 
                            type="button" 
                            className="feedback-cancel-btn" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="feedback-submit-btn" 
                            disabled={isSubmitting}
                        >
                            {isSubmitting 
                                ? 'Sending...' 
                                : feedbackType === 'feedback' 
                                    ? 'Send Feedback' 
                                    : 'Report Issue'
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default FeedbackModal;